import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Grid
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Header from "../Header/Header1";

const simpleTradeDoc = `
Simple Trading API (OMS) – Brief Overview

The Simple Trading API provides a low-level Order Management System (OMS) that allows users to create, manage, and close trades across multiple exchanges using a unified format.
It removes the need to handle exchange-specific APIs by translating standardized trade commands into exchange-compatible orders.

The system supports advanced order types such as Absolute Trailing, Relative Trailing, Conditional Market, Conditional Limit, and Conditional Trailing.
If an exchange does not support a certain order type, the OMS emulates the behavior automatically.

It can also recreate canceled orders, adjust order size if the balance is insufficient, and automatically handle lot size and precision requirements.

In this system, a Trade is the main entity, and a single trade may include one or multiple exchange orders executed on the connected exchange.


1. Pair
The trading pair that will be used for the order (e.g., USDT_BTC).
It defines which asset you are buying or selling against another asset.

2. Order Type
Determines how the order will be executed:
Market – executes immediately at the current market price.
Limit – executes only when the market reaches the specified price.

3. Side
Specifies the direction of the trade:
Buy – purchase the base asset.
Sell – sell the base asset.

4. Quantity
The amount of the base asset to buy or sell in the trade.

5. Price (Limit Order Only)
The price at which the order should execute when using a limit order.

6. Total
Displays the total trade value (usually quantity × price or calculated using the current market price).

7. Conditional Price
Optional trigger price that must be reached before the trade is executed.

8. Conditional Value
Defines the condition used for the trigger:
less_or_equal – execute when price is less than or equal to the trigger.
greater_or_equal – execute when price is greater than or equal to the trigger.

9. Conditional Type
Specifies which market price should be used for the trigger:
ask – sell price
bid – buy price
last – last traded price

10. Trailing Type & Trailing Value
Used to enable trailing execution logic:
value – trailing distance based on a fixed value.
percent – trailing distance based on percentage.

11. Timeout
Defines how long the conditional order should remain active before it automatically cancels.
`;


const dcaBotDoc = `
DCA Bot – Overview

A DCA Bot (Dollar Cost Averaging Bot) is an automated trading tool that executes trades on an exchange based on a predefined DCA strategy. Instead of placing a single order, the bot spreads purchases across multiple orders to average the entry price over time.

A DCA Bot entity stores all the configuration and rules required to manage trading deals automatically. It contains information such as the exchange account, trading pairs, strategy parameters, and risk-management settings.

Key components of a DCA Bot include base orders, safety orders, take profit, and stop loss. The base order opens the initial trade, while safety orders are placed at lower prices to average down the entry price if the market moves against the position.

The bot continuously monitors the market and automatically opens and manages deals according to the configured strategy. It also supports features such as trading limits, cooldown periods between deals, and automatic profit-taking.

1. Name
A custom name used to identify the DCA bot configuration.

2. Base Order Volume Type
Defines how the base order amount is measured:
Quote Currency – order value in quote asset (e.g., USDT).
Base Currency – order value in base asset (e.g., BTC).

3. Pairs
The trading pair the bot will trade on (e.g., BTC_USDT).

4. Strategy List
Selects the entry signal strategies that determine when a new deal starts.

5. Start Order Type
Defines how the initial order is executed:
Market – executes immediately at market price.
Limit – executes at a specified price.

6. Base Order Volume
The size of the first order placed when a new deal starts.

7. Take Profit Type
Defines how profit is calculated:
Total – profit based on the entire deal size.
Base – profit based only on the base order.

8. Take Profit (%)
The profit percentage target at which the bot will close the deal.

9. Risk Reduction Percentage
Reduces exposure by adjusting order behavior when the market moves against the position.

10. Safety Order Step Percentage
Percentage price movement required before placing the next safety order.

11. Safety Order Volume
The size of each safety order used to average the entry price.

12. Max Safety Orders
Maximum number of safety orders allowed in a deal.

13. Active Safety Orders Count
Number of safety orders that can be active simultaneously.

14. Martingale Volume Coefficient
Multiplier used to increase safety order size progressively.

15. Martingale Step Coefficient
Multiplier used to increase the distance between safety orders.

16. Min Price for New Deals
Minimum price allowed for the bot to start a new deal.

17. Max Price for New Deals
Maximum price allowed for the bot to start a new deal.

18. Profit Currency
Determines which currency the profit is received in:
Base currency
Quote currency

19. Strategy
Defines trade direction:
Long – buy first, sell later.
Short – sell first, buy later.

20. Stop Loss Type
Defines how losses are handled:
Stop Loss – closes the deal when loss limit is reached.
Stop Loss and Disable Bot – closes the deal and stops the bot from opening new deals.
`;
const gridBotDoc = `
Grid Bot – Overview

A Grid Bot is an automated trading tool that places multiple buy and sell orders within a predefined price range to profit from market fluctuations. It divides the selected price range into several levels (grids) and automatically executes trades as the market price moves between these levels.

The Grid Bot entity stores configuration details such as the trading pair, strategy type, and grid settings, which define how the bot places orders. It also tracks performance metrics including current profit, total profit, and invested amounts in both base and quote currencies.

Additionally, the bot maintains status information like whether it is enabled or disabled, along with optional stop-loss protection to manage risk.

Using the available tools, users can create, configure, monitor, and optimize Grid Bots to automatically trade market volatility without continuous manual intervention.


1. Name
A custom name used to identify the Grid Bot configuration.

2. Pairs
The trading pair the bot will operate on (for example BTC_USDT).
All grid orders will be placed for this pair.

3. Upper Price
The maximum price limit of the grid range.
The bot will stop placing buy orders above this level.

4. Lower Price
The minimum price limit of the grid range.
The bot will stop placing sell orders below this level.

5. Quantity
The amount allocated for trading across the grid levels.

6. Grid Quantity
Defines how many grid levels will exist between the upper and lower price.
More grids = smaller price gaps and more frequent trades.

7. Grid Type
Defines how grid levels are spaced:
Arithmetic – equal price difference between each grid level.
Geometric – equal percentage difference between grid levels.

8. Upper Stop Loss
Price level where the bot stops trading if the price rises above the grid range to limit losses.

9. Lower Stop Loss
Price level where the bot stops trading if the price drops below the grid range.

10. Trailing Up Price
Moves the entire grid upward automatically when the market continues rising beyond the grid range.

11. Trailing Down Price
Moves the entire grid downward automatically when the market price keeps falling.

12. Expansion Up Price
Expands the upper boundary of the grid when price moves above the current range.

13. Expansion Down Price
Expands the lower boundary of the grid when price moves below the current range.
`;

const smartTradeDoc = `
SmartTrade – Overview

SmartTrade is an advanced trading tool that allows users to open, manage, and close trades on an exchange using predefined trading conditions. It provides greater control over individual trades compared to basic order placement.

A SmartTrade entity stores all the configuration needed for a trade, including the exchange account, trading pair, and position details. Users can define automated conditions such as Take Profit, Stop Loss, closing trades at market price, and reducing funds during a trade.

Once the SmartTrade is configured, the system automatically manages the associated trades according to the defined rules. This allows traders to automate risk management and profit targets without constant monitoring.

SmartTrade supports multiple trade types within a single strategy, including position trades, take-profit trades, stop-loss trades, reduce-funds trades, and market-close trades, enabling flexible and controlled trading execution.


1. Pair
The trading pair used for the trade (e.g., BTC_USDT).
All orders in this SmartTrade will execute for this pair.

2. Position Units (Quantity)
The amount of the asset you want to trade in the position.

3. Position Order Type
Defines how the entry order will execute:
Market – executes immediately at current market price.
Limit – executes only when the specified price is reached.

4. Position Type
Defines the direction of the trade:
Buy – opens a long position.
Sell – opens a short or sell position.

5. Position Price
The price at which the position order will execute when using a limit order.


Conditional Orders

6. Conditional Order
Allows the trade to start only when a certain price condition is met.

7. Conditional Order Type
Defines how the order will execute once the condition triggers:
Market
Limit

8. Conditional Price
The trigger price required before the position order is placed.


Take Profit Settings

9. Take Profit
Enables automatic closing of the trade when the target profit is reached.

10. Take Profit Order Type
Defines how the take-profit order executes:
Market
Limit

11. Take Profit Price
The target price where the trade will close to secure profit.

12. Take Profit Type
Specifies which market price is used for the trigger:
Ask
Bid
Last

13. Take Profit Trailing
Allows the take-profit level to follow the market price when the trade becomes profitable.

14. Take Profit Trailing Distance
Defines the distance (percentage) the trailing take profit follows the price.


Stop Loss Settings

15. Stop Loss
Automatically closes the trade when the price moves against the position.

16. Stop Loss Order Type
Defines how the stop-loss order executes:
Market
Limit

17. Stop Loss Price
The price where the trade will close to prevent further loss.

18. Stop Loss Price Type
Defines which price is used to trigger stop loss:
Bid
Ask
Last

19. Stop Loss Timeout
Adds a delay before executing the stop-loss order.

20. Timeout Seconds
The number of seconds to wait before executing stop loss.

21. Trailing Stop Loss
Allows the stop-loss level to move with the price when the trade becomes profitable.

22. Trailing Percentage
Defines how far the stop loss trails behind the market price.

23. Move Stop to Breakeven
Automatically moves the stop-loss to the entry price once the trade becomes profitable.

24. Note
Optional comment or description for the SmartTrade.
Used to store additional information about the trade.
`;


const Tutorial = () => {
    return (
        <div className="market-full ar3 page_align" style={{ maxHeight: "100vh" }}>
            <Header />

            <div style={{ padding: "20px" }}>
                <Typography variant="h4">Bot Document</Typography>
            </div>

            <Grid container justifyContent="center" className="mb-20">
                <Grid item xs={12} md={10}>

                    <Accordion className="tutorial-accordion">

                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Simple Trade</Typography>
                        </AccordionSummary>

                        <AccordionDetails>

                            <pre
                                style={{
                                    whiteSpace: "pre-wrap",
                                    lineHeight: "1.7",
                                    color: "var(--clr-font)",
                                    fontFamily: "inherit"
                                }}
                            >
                                {simpleTradeDoc}
                            </pre>

                        </AccordionDetails>

                    </Accordion>

                    <Accordion className="tutorial-accordion">

                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">DCA Bot</Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                            <pre
                                style={{
                                    whiteSpace: "pre-wrap",
                                    lineHeight: "1.8",
                                    color: "var(--clr-font)",
                                    fontFamily: "inherit"
                                }}
                            >
                                {dcaBotDoc}
                            </pre>
                        </AccordionDetails>

                    </Accordion>

                    {/* GRID BOT */}

                    <Accordion className="tutorial-accordion">

                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Grid Bot</Typography>
                        </AccordionSummary>

                        <AccordionDetails>

                            <pre
                                style={{
                                    whiteSpace: "pre-wrap",
                                    lineHeight: "1.8",
                                    color: "var(--clr-font)",
                                    fontFamily: "inherit"
                                }}
                            >
                                {gridBotDoc}
                            </pre>

                        </AccordionDetails>

                    </Accordion>

                    {/* SMART TRADE */}

                    <Accordion className="tutorial-accordion">

                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">SmartTrade</Typography>
                        </AccordionSummary>

                        <AccordionDetails>

                            <pre
                                style={{
                                    whiteSpace: "pre-wrap",
                                    lineHeight: "1.8",
                                    color: "var(--clr-font)",
                                    fontFamily: "inherit"
                                }}
                            >
                                {smartTradeDoc}
                            </pre>

                        </AccordionDetails>

                    </Accordion>

                </Grid>
            </Grid>
        </div>
    );
};

export default Tutorial;